// The hero: a month of meetings drawn as a familiar calendar. Every past
// meeting carries its reaction tally and score, so a first-time visitor
// sees what the product does and what "getting better" looks like. Pure
// string builder so the social-image template can reuse it.

export type Event = {
  time: string
  title: string
  main?: boolean
  up?: number; neutral?: number; down?: number
  guests?: boolean
}
export type Day = { n: number; today?: boolean; events: Event[] }

export const MONTH = 'March 2026'
export const SERIES = 'Weekly product sync, Mondays 10:00'
export const WEEKS: Day[][] = [
  [
    { n: 2, events: [{ time: '10:00', title: 'Weekly product sync', main: true, up: 2, neutral: 3, down: 4 }] },
    { n: 3, events: [{ time: '14:00', title: '1:1 with Ana' }] },
    { n: 4, events: [{ time: '11:00', title: 'Design review', up: 4, neutral: 3, down: 1 }] },
    { n: 5, events: [] },
    { n: 6, events: [{ time: '15:00', title: 'Retro', up: 6, neutral: 2, down: 0 }] },
  ],
  [
    { n: 9, events: [{ time: '10:00', title: 'Weekly product sync', main: true, up: 4, neutral: 3, down: 2 }] },
    { n: 10, events: [] },
    { n: 11, events: [{ time: '11:00', title: 'Design review', up: 5, neutral: 2, down: 0 }] },
    { n: 12, events: [{ time: '09:30', title: 'Hiring sync' }] },
    { n: 13, events: [] },
  ],
  [
    { n: 16, events: [{ time: '10:00', title: 'Weekly product sync', main: true, up: 6, neutral: 2, down: 1 }] },
    { n: 17, events: [{ time: '14:00', title: '1:1 with Ana' }] },
    { n: 18, events: [] },
    { n: 19, events: [{ time: '13:00', title: 'Roadmap', up: 3, neutral: 4, down: 2 }] },
    { n: 20, events: [{ time: '15:00', title: 'Retro', up: 7, neutral: 1, down: 0 }] },
  ],
  [
    { n: 23, today: true, events: [{ time: '10:00', title: 'Weekly product sync', main: true, up: 7, neutral: 1, down: 0 }] },
    { n: 24, events: [] },
    { n: 25, events: [{ time: '11:00', title: 'Design review', guests: true }] },
    { n: 26, events: [] },
    { n: 27, events: [{ time: '15:00', title: 'Retro' }] },
  ],
]

export function score(e: Event): number | null {
  const t = (e.up ?? 0) + (e.neutral ?? 0) + (e.down ?? 0)
  if (t < 3) return null
  return Math.round((((e.up ?? 0) + 0.5 * (e.neutral ?? 0)) / t) * 100)
}
const band = (s: number) => (s >= 70 ? 'good' : s >= 50 ? 'mid' : 'bad')

// Small stroke glyphs (Lucide shapes) so the tallies read as UI, not emoji.
const UP = '<svg viewBox="0 0 24 24" aria-label="thumbs up"><path d="M7 10v12"/><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/></svg>'
const NEUTRAL = '<svg viewBox="0 0 24 24" aria-label="neutral"><circle cx="12" cy="12" r="10"/><path d="M8 15h8"/><path d="M9 9h.01"/><path d="M15 9h.01"/></svg>'
const DOWN = '<svg viewBox="0 0 24 24" aria-label="thumbs down"><path d="M17 14V2"/><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z"/></svg>'
const tally = (icon: string, n?: number) => `<span class="tally">${icon}${n ?? 0}</span>`

export function calendarHtml(): string {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
  let order = 0
  const cells = WEEKS.flatMap((week, w) => week.map((d, i) => {
    const evs = d.events.map(e => {
      const s = score(e)
      const res = s === null ? '' : `<div class="res" style="--o:${order++}"><span class="s ${band(s)}">${s}</span>${tally(UP, e.up)}${tally(NEUTRAL, e.neutral)}${tally(DOWN, e.down)}</div>`
      const guests = e.guests ? `<div class="guest"><b>Guests</b><span class="av"></span><span class="av"></span><span class="av"></span><span class="av"></span> +3 <span class="bot">✓ bot@calendarfeedback.com</span></div>` : ''
      return `<div class="ev${e.main ? ' main' : ''}"><span class="t">${e.time}</span>${e.title}</div>${res}${guests}`
    }).join('')
    return `<div class="cell${d.today ? ' today' : ''}" data-day="${days[i]}" data-week="${w + 1}"><div class="d">${d.n}</div>${evs}</div>`
  }))
  const mains = WEEKS.map(w => score(w[0].events[0])).filter((s): s is number => s !== null)
  return `<div class="cal" role="img" aria-label="A month of meetings. The weekly product sync scored ${mains.join(', ')} across four Mondays.">
<div class="cal-top"><div>${MONTH}</div><span>${SERIES}</span></div>
<div class="days">${days.map(d => `<div data-day="${d}">${d}</div>`).join('')}</div>
<div class="grid">${cells.join('')}</div>
<div class="legend"><span><b>Weekly product sync</b> went ${mains.join(', ')} in four weeks.</span><span>Scores appear 2 h after a meeting ends.</span><span>Nobody sees who voted what.</span></div>
</div>`
}
